import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

export type TaskType = 'call' | 'email' | 'meeting' | 'task' | 'whatsapp'
export type TaskStatusKind = 'pending' | 'done' | 'cancelled'

export interface Task {
  id: string
  title: string
  description: string | null
  type: TaskType
  status: TaskStatusKind
  lead_id: string | null
  opportunity_id: string | null
  assigned_to: string
  assigned_to_name: string | null
  created_by: string
  created_by_name: string | null
  due_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
}

export interface TaskCreate {
  title: string
  description?: string | null
  type?: TaskType
  lead_id?: string | null
  opportunity_id?: string | null
  assigned_to?: string | null
  due_at?: string | null
}

export interface TaskUpdate {
  title?: string
  description?: string | null
  type?: TaskType
  status?: TaskStatusKind
  assigned_to?: string
  due_at?: string | null
}

interface ListParams {
  lead_id?: string
  opportunity_id?: string
  assigned_to?: string
  status?: TaskStatusKind
  overdue?: boolean
}

export function useTasks(params: ListParams = {}) {
  return useQuery({
    queryKey: ['tasks', params],
    queryFn: async () => {
      const { data } = await api.get<{ items: Task[]; total: number }>('/v1/tasks', { params })
      return data
    },
    enabled: !!(params.lead_id || params.opportunity_id || params.assigned_to),
  })
}

export function useCreateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: TaskCreate) => {
      const { data } = await api.post<Task>('/v1/tasks', body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  })
}

export function useUpdateTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: body }: { id: string; data: TaskUpdate }) => {
      const { data } = await api.put<Task>(`/v1/tasks/${id}`, body)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  })
}

export function useCompleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<Task>(`/v1/tasks/${id}/complete`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  })
}

export function useReopenTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<Task>(`/v1/tasks/${id}/reopen`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  })
}

export function useDeleteTask() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/v1/tasks/${id}`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] }),
  })
}
